/**
 * PrayerTimeService -- Server-first prayer times with Adhan.js offline fallback.
 *
 * Primary: fetch from /aweni/api/v1/prayer/times (Aladhan.com cached, DST-aware)
 * Fallback: Adhan.js local calculation (offline mode)
 *
 * Server times are cached in IndexedDB for offline use.
 */

import {
  PrayerTimes as AdhanPrayerTimes,
  CalculationMethod,
  CalculationParameters,
  Coordinates,
  HighLatitudeRule,
  Madhab,
  Qibla,
  SunnahTimes,
} from 'adhan';
import type { Lang } from '../types';
import { NativeService } from './NativeService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PrayerSource = 'server' | 'offline-cache' | 'local-calc';

export interface PrayerTimesData {
  fajr: Date;
  sunrise: Date;
  dhuhr: Date;
  asr: Date;
  maghrib: Date;
  isha: Date;
  nextPrayer: { name: string; time: Date; minutesUntil: number } | null;
  qiblaDirection: number;
  location: { lat: number; lng: number; city?: string };
  method: string;
  source: PrayerSource;
  hijri?: { date: string; day: string; month: string; month_ar: string; year: string };
}

export interface NextPrayer {
  name: string;
  time: Date;
  minutesUntil: number;
}

export interface PrayerReminderConfig {
  fajr: number;
  sunrise: number;
  dhuhr: number;
  asr: number;
  maghrib: number;
  isha: number;
}

export type CalculationMethodKey =
  | 'MuslimWorldLeague'
  | 'ISNA'
  | 'Egypt'
  | 'MakkahUmmAlQura'
  | 'Karachi'
  | 'Tehran'
  | 'Singapore'
  | 'MoonsightingCommittee'
  | 'Dubai'
  | 'Qatar'
  | 'Kuwait'
  | 'Turkey';

export type MadhabKey = 'Shafi' | 'Hanafi';

// Map calculation method to Aladhan method ID (https://aladhan.com/calculation-methods)
const METHOD_TO_ALADHAN: Record<CalculationMethodKey, number> = {
  MuslimWorldLeague: 3,
  ISNA: 2,
  Egypt: 5,
  MakkahUmmAlQura: 4,
  Karachi: 1,
  Tehran: 7,
  Singapore: 11,
  MoonsightingCommittee: 15,
  Dubai: 16,
  Qatar: 10,
  Kuwait: 9,
  Turkey: 13,
};

// ISO country code → recommended calc method (rough, expandable).
// Source: defaults used by Muslim Pro / IslamicFinder per region.
// Exposed via getRecommendedMethodForCountry() so the location flow can
// auto-pick a sensible default the first time the user grants location.
export const COUNTRY_TO_METHOD: Record<string, CalculationMethodKey> = {
  US: 'ISNA', CA: 'ISNA',
  GB: 'MoonsightingCommittee', IE: 'MoonsightingCommittee',
  EG: 'Egypt', DZ: 'Egypt', LY: 'Egypt', SD: 'Egypt',
  SA: 'MakkahUmmAlQura', YE: 'MakkahUmmAlQura', BH: 'MakkahUmmAlQura', OM: 'MakkahUmmAlQura',
  AE: 'Dubai',
  QA: 'Qatar',
  KW: 'Kuwait',
  PK: 'Karachi', IN: 'Karachi', BD: 'Karachi', AF: 'Karachi',
  SG: 'Singapore', MY: 'Singapore', ID: 'Singapore', BN: 'Singapore',
  IR: 'Tehran',
  TR: 'Turkey',
};

// ---------------------------------------------------------------------------
// Prayer name i18n
// ---------------------------------------------------------------------------

type PrayerKey = 'fajr' | 'sunrise' | 'dhuhr' | 'asr' | 'maghrib' | 'isha';

const PRAYER_NAMES: Record<PrayerKey, Record<Lang, string>> = {
  fajr: { en: 'Fajr', fr: 'Fajr', ar: '\u0627\u0644\u0641\u062c\u0631', es: 'Fajr', de: 'Fadschr' },
  sunrise: {
    en: 'Sunrise',
    fr: 'Lever du soleil',
    ar: '\u0627\u0644\u0634\u0631\u0648\u0642',
    es: 'Amanecer',
    de: 'Sonnenaufgang',
  },
  dhuhr: { en: 'Dhuhr', fr: 'Dhuhr', ar: '\u0627\u0644\u0638\u0647\u0631', es: 'Dhuhr', de: 'Dhuhr' },
  asr: { en: 'Asr', fr: 'Asr', ar: '\u0627\u0644\u0639\u0635\u0631', es: 'Asr', de: 'Asr' },
  maghrib: { en: 'Maghrib', fr: 'Maghrib', ar: '\u0627\u0644\u0645\u063a\u0631\u0628', es: 'Maghrib', de: 'Maghrib' },
  isha: { en: 'Isha', fr: 'Isha', ar: '\u0627\u0644\u0639\u0634\u0627\u0621', es: 'Isha', de: 'Ischa' },
};

const PRAYER_ORDER: PrayerKey[] = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'];

// ---------------------------------------------------------------------------
// Storage keys
// ---------------------------------------------------------------------------

const LS_LOCATION = 'aweni_prayer_location';
const LS_METHOD = 'aweni_prayer_method';
const LS_MADHAB = 'aweni_prayer_madhab';
const LS_REMINDERS = 'aweni_prayer_reminders';
const IDB_STORE = 'aweni_prayer_cache';

// ---------------------------------------------------------------------------
// Server API config
// ---------------------------------------------------------------------------

const PRAYER_API_BASE = '/aweni/api/v1/prayer';

// ---------------------------------------------------------------------------
// IndexedDB cache helpers
// ---------------------------------------------------------------------------

function openPrayerDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open('aweni_prayer', 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE, { keyPath: 'cacheKey' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function readIDBCache(key: string): Promise<unknown | null> {
  try {
    const db = await openPrayerDB();
    return new Promise((resolve) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const store = tx.objectStore(IDB_STORE);
      const req = store.get(key);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

async function writeIDBCache(key: string, data: Record<string, unknown>): Promise<void> {
  try {
    const db = await openPrayerDB();
    const tx = db.transaction(IDB_STORE, 'readwrite');
    const store = tx.objectStore(IDB_STORE);
    store.put({ ...data, cacheKey: key, cachedAt: Date.now() });
  } catch {
    // Silently fail
  }
}

// ---------------------------------------------------------------------------
// Calculation method map
// ---------------------------------------------------------------------------

function getCalculationParams(
  method: CalculationMethodKey,
  madhab: MadhabKey,
  coords: Coordinates,
): CalculationParameters {
  let params: CalculationParameters;
  switch (method) {
    case 'MuslimWorldLeague': params = CalculationMethod.MuslimWorldLeague(); break;
    case 'ISNA': params = CalculationMethod.NorthAmerica(); break;
    case 'Egypt': params = CalculationMethod.Egyptian(); break;
    case 'MakkahUmmAlQura': params = CalculationMethod.UmmAlQura(); break;
    case 'Karachi': params = CalculationMethod.Karachi(); break;
    case 'Tehran': params = CalculationMethod.Tehran(); break;
    case 'Singapore': params = CalculationMethod.Singapore(); break;
    case 'MoonsightingCommittee': params = CalculationMethod.MoonsightingCommittee(); break;
    case 'Dubai': params = CalculationMethod.Dubai(); break;
    case 'Qatar': params = CalculationMethod.Qatar(); break;
    case 'Kuwait': params = CalculationMethod.Kuwait(); break;
    case 'Turkey': params = CalculationMethod.Turkey(); break;
    default: params = CalculationMethod.MuslimWorldLeague();
  }
  params.madhab = madhab === 'Hanafi' ? Madhab.Hanafi : Madhab.Shafi;
  // Auto-pick high-latitude rule based on the location (SeventhOfTheNight above ~48°)
  params.highLatitudeRule = HighLatitudeRule.recommended(coords);
  return params;
}

// ---------------------------------------------------------------------------
// Parse "HH:MM" time string to Date for today
// ---------------------------------------------------------------------------

function parseTimeToDate(timeStr: string, dateStr?: string): Date {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const d = dateStr ? new Date(dateStr + 'T00:00:00') : new Date();
  d.setHours(hours, minutes, 0, 0);
  return d;
}

// ---------------------------------------------------------------------------
// PrayerTimeService
// ---------------------------------------------------------------------------

export class PrayerTimeService {
  private location: { lat: number; lng: number; city?: string } | null = null;
  private method: CalculationMethodKey = 'MuslimWorldLeague';
  private madhab: MadhabKey = 'Shafi';
  private reminderOffsets: PrayerReminderConfig = {
    fajr: 15,
    sunrise: 0,
    dhuhr: 10,
    asr: 10,
    maghrib: 5,
    isha: 10,
  };
  private cachedServerTimes: Record<string, unknown> | null = null;
  private cachedAdhanTimes: AdhanPrayerTimes | null = null;
  private cachedDate: string | null = null;
  private lastSource: PrayerSource = 'local-calc';
  private countdownInterval: ReturnType<typeof setInterval> | null = null;
  private scheduledNotifications: number[] = [];
  private locale: Lang = 'en';
  private serverFetchPromise: Promise<void> | null = null;

  constructor() {
    this.loadFromStorage();
  }

  // ---- Lifecycle ----------------------------------------------------------

  async initialize(): Promise<void> {
    if (!this.location) {
      await this.requestGeolocation();
    }

    if (this.location) {
      // Try server first, then fall back to local
      await this.fetchServerTimes();
      if (!this.cachedServerTimes) {
        this.computeLocalTimes();
      }
      this.scheduleReminders();
    }
  }

  // ---- Public API ---------------------------------------------------------

  getTodayTimes(): PrayerTimesData | null {
    if (!this.location) return null;

    // Prefer server times
    if (this.cachedServerTimes) {
      return this.buildFromServerTimes(this.cachedServerTimes);
    }

    // Fallback to Adhan.js
    const times = this.getAdhanTimes();
    if (!times) return null;

    const next = this.computeNextPrayer(times);

    return {
      fajr: times.fajr,
      sunrise: times.sunrise,
      dhuhr: times.dhuhr,
      asr: times.asr,
      maghrib: times.maghrib,
      isha: times.isha,
      nextPrayer: next ?? this.computeTomorrowFajr(),
      qiblaDirection: this.getQiblaDirection(),
      location: { ...this.location },
      method: this.method,
      source: 'local-calc',
    };
  }

  getNextPrayer(): NextPrayer | null {
    const data = this.getTodayTimes();
    if (!data) return null;
    return data.nextPrayer;
  }

  getSource(): PrayerSource {
    return this.lastSource;
  }

  getQiblaDirection(): number {
    if (!this.location) return 0;
    const coords = new Coordinates(this.location.lat, this.location.lng);
    return Qibla(coords);
  }

  setMethod(method: CalculationMethodKey): void {
    this.method = method;
    localStorage.setItem(LS_METHOD, method);
    this.cachedServerTimes = null;
    this.cachedAdhanTimes = null;
    this.cachedDate = null;
    // Re-fetch from server with new method
    this.fetchServerTimes().then(() => this.scheduleReminders());
  }

  setMadhab(madhab: MadhabKey): void {
    this.madhab = madhab;
    localStorage.setItem(LS_MADHAB, madhab);
    this.cachedServerTimes = null;
    this.cachedAdhanTimes = null;
    this.cachedDate = null;
    this.fetchServerTimes().then(() => this.scheduleReminders());
  }

  getMadhab(): MadhabKey {
    return this.madhab;
  }

  setLocation(lat: number, lng: number, city?: string, countryCode?: string): void {
    this.location = { lat, lng, city };
    localStorage.setItem(LS_LOCATION, JSON.stringify(this.location));
    this.cachedServerTimes = null;
    this.cachedAdhanTimes = null;
    this.cachedDate = null;

    // Auto-pick a country-aware calc method on first run only — never
    // override a method the user has explicitly chosen (we know that
    // happened because LS_METHOD is then present in localStorage).
    if (countryCode && !localStorage.getItem(LS_METHOD)) {
      const recommended = COUNTRY_TO_METHOD[countryCode.toUpperCase()];
      if (recommended) {
        this.method = recommended;
      }
    }

    this.fetchServerTimes().then(() => this.scheduleReminders());
  }

  /** Returns the recommended calc method for an ISO 3166-1 alpha-2 country
   *  code, or undefined if none is mapped. Use this from the location flow
   *  to suggest a default; user choice always takes precedence. */
  static getRecommendedMethodForCountry(code: string | null | undefined): CalculationMethodKey | undefined {
    if (!code) return undefined;
    return COUNTRY_TO_METHOD[code.toUpperCase()];
  }

  setReminderOffset(prayer: string, minutes: number): void {
    const key = prayer.toLowerCase() as PrayerKey;
    if (key in this.reminderOffsets) {
      this.reminderOffsets[key] = Math.max(0, Math.min(60, minutes));
      localStorage.setItem(LS_REMINDERS, JSON.stringify(this.reminderOffsets));
      this.scheduleReminders();
    }
  }

  getReminderOffsets(): PrayerReminderConfig {
    return { ...this.reminderOffsets };
  }

  scheduleReminders(): void {
    this.clearScheduledNotifications();

    const data = this.getTodayTimes();
    if (!data) return;

    const now = Date.now();
    const prayerEntries: Array<{ key: PrayerKey; time: Date }> = [
      { key: 'fajr', time: data.fajr },
      { key: 'sunrise', time: data.sunrise },
      { key: 'dhuhr', time: data.dhuhr },
      { key: 'asr', time: data.asr },
      { key: 'maghrib', time: data.maghrib },
      { key: 'isha', time: data.isha },
    ];

    // On native platforms, use Capacitor LocalNotifications (persistent, survives background)
    if (NativeService.isNative()) {
      for (const { key, time } of prayerEntries) {
        const offsetMinutes = this.reminderOffsets[key];
        const name = this.getPrayerName(key);
        const timeStr = this.formatTime(time);
        // Stable IDs: prayer index * 10 + type (0=reminder, 1=athan)
        const prayerIdx = PRAYER_ORDER.indexOf(key);

        if (offsetMinutes > 0) {
          const reminderAt = new Date(time.getTime() - offsetMinutes * 60_000);
          if (reminderAt.getTime() > now) {
            NativeService.scheduleLocalNotification({
              title: `${name} in ${offsetMinutes} minutes`,
              body: `${name} at ${timeStr}`,
              at: reminderAt,
              id: 5000 + prayerIdx * 10,
            });
          }
        }

        // Athan notification at exact prayer time
        if (time.getTime() > now) {
          NativeService.scheduleLocalNotification({
            title: name,
            body: `It is time for ${name} prayer`,
            at: time,
            id: 5001 + prayerIdx * 10,
          });
        }
      }
      return;
    }

    // Web fallback: setTimeout + Notification API
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      return;
    }

    for (const { key, time } of prayerEntries) {
      const offsetMinutes = this.reminderOffsets[key];
      if (offsetMinutes <= 0) continue;

      const reminderTime = time.getTime() - offsetMinutes * 60_000;
      const delay = reminderTime - now;

      if (delay > 0 && delay < 24 * 60 * 60_000) {
        const timeoutId = window.setTimeout(() => {
          this.showPrayerNotification(key, time);
        }, delay);
        this.scheduledNotifications.push(timeoutId);
      }

      const athanDelay = time.getTime() - now;
      if (athanDelay > 0 && athanDelay < 24 * 60 * 60_000) {
        const athanId = window.setTimeout(() => {
          this.showAthanNotification(key);
        }, athanDelay);
        this.scheduledNotifications.push(athanId);
      }
    }
  }

  startCountdown(callback: (data: { minutesUntil: number; secondsUntil: number }) => void): () => void {
    const tick = () => {
      const np = this.getNextPrayer();
      if (np) {
        const data = this.getTodayTimes();
        let prayerTime = np.time;
        if (data) {
          const key = np.name as keyof Pick<PrayerTimesData, 'fajr' | 'sunrise' | 'dhuhr' | 'asr' | 'maghrib' | 'isha'>;
          if (key in data) prayerTime = data[key];
        }
        const diffMs = prayerTime.getTime() - Date.now();
        const secondsUntil = Math.max(0, Math.floor(diffMs / 1000));
        callback({ minutesUntil: np.minutesUntil, secondsUntil });
      } else {
        callback({ minutesUntil: -1, secondsUntil: -1 });
      }
    };

    tick();

    const interval = setInterval(tick, 1000); // Update every second for live countdown
    this.countdownInterval = interval;

    return () => {
      clearInterval(interval);
      if (this.countdownInterval === interval) {
        this.countdownInterval = null;
      }
    };
  }

  setLocale(locale: Lang): void {
    this.locale = locale;
  }

  getPrayerName(key: PrayerKey, lang?: Lang): string {
    const l = lang ?? this.locale;
    return PRAYER_NAMES[key]?.[l] ?? PRAYER_NAMES[key]?.en ?? key;
  }

  getAllPrayerNames(lang?: Lang): Record<PrayerKey, string> {
    const l = lang ?? this.locale;
    const result = {} as Record<PrayerKey, string>;
    for (const key of PRAYER_ORDER) {
      result[key] = PRAYER_NAMES[key][l];
    }
    return result;
  }

  formatTime(date: Date, lang?: Lang): string {
    const l = lang ?? this.locale;
    const use24h = l === 'fr' || l === 'de';
    try {
      return date.toLocaleTimeString(l === 'ar' ? 'ar-SA' : l, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: !use24h,
      });
    } catch {
      return date.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  }

  getTimesForDate(date: Date): PrayerTimesData | null {
    if (!this.location) return null;

    const coords = new Coordinates(this.location.lat, this.location.lng);
    const params = getCalculationParams(this.method, this.madhab, coords);
    const times = new AdhanPrayerTimes(coords, date, params);

    const next = this.computeNextPrayer(times);

    return {
      fajr: times.fajr,
      sunrise: times.sunrise,
      dhuhr: times.dhuhr,
      asr: times.asr,
      maghrib: times.maghrib,
      isha: times.isha,
      nextPrayer: next ?? this.computeTomorrowFajr(),
      qiblaDirection: this.getQiblaDirection(),
      location: { ...this.location },
      method: this.method,
      source: 'local-calc',
    };
  }

  /** Returns tomorrow's Fajr as the next prayer when today's are all done. */
  private computeTomorrowFajr(): NextPrayer | null {
    if (!this.location) return null;
    const coords = new Coordinates(this.location.lat, this.location.lng);
    const params = getCalculationParams(this.method, this.madhab, coords);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const t = new AdhanPrayerTimes(coords, tomorrow, params);
    const minutesUntil = Math.round((t.fajr.getTime() - Date.now()) / 60_000);
    return { name: 'fajr', time: t.fajr, minutesUntil };
  }

  getSunnahTimes(): { middleOfTheNight: Date; lastThirdOfTheNight: Date } | null {
    const times = this.getAdhanTimes();
    if (!times) return null;

    const sunnah = new SunnahTimes(times);
    return {
      middleOfTheNight: sunnah.middleOfTheNight,
      lastThirdOfTheNight: sunnah.lastThirdOfTheNight,
    };
  }

  getLocation(): { lat: number; lng: number; city?: string } | null {
    return this.location ? { ...this.location } : null;
  }

  getMethod(): CalculationMethodKey {
    return this.method;
  }

  isReady(): boolean {
    return this.location !== null;
  }

  destroy(): void {
    this.clearScheduledNotifications();
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  // ---- Server fetch -------------------------------------------------------

  private async fetchServerTimes(): Promise<void> {
    if (!this.location) return;
    if (this.serverFetchPromise) return this.serverFetchPromise;

    this.serverFetchPromise = this._doFetchServerTimes();
    try {
      await this.serverFetchPromise;
    } finally {
      this.serverFetchPromise = null;
    }
  }

  private async _doFetchServerTimes(): Promise<void> {
    if (!this.location) return;

    const date = new Date().toISOString().split('T')[0];
    const methodId = METHOD_TO_ALADHAN[this.method] ?? 3;
    const cacheKey = `${this.location.lat.toFixed(2)}_${this.location.lng.toFixed(2)}_${date}_m${methodId}`;

    try {
      const url = `${PRAYER_API_BASE}/times?lat=${this.location.lat}&lng=${this.location.lng}&date=${date}&method=${methodId}`;
      const resp = await fetch(url, { cache: 'no-store' });

      if (resp.ok) {
        const json = await resp.json();
        if (json.ok && json.data?.timings) {
          this.cachedServerTimes = json.data;
          this.lastSource = 'server';
          // Save to IndexedDB for offline
          await writeIDBCache(cacheKey, json.data);
          return;
        }
      }
    } catch {
      // Network error -- try IndexedDB cache
    }

    // Try IndexedDB offline cache
    const cached = await readIDBCache(cacheKey) as Record<string, unknown> | null;
    if (cached && cached.timings) {
      this.cachedServerTimes = cached;
      this.lastSource = 'offline-cache';
      return;
    }

    // Fall back to Adhan.js local calculation
    this.cachedServerTimes = null;
    this.lastSource = 'local-calc';
    this.computeLocalTimes();
  }

  private buildFromServerTimes(serverData: Record<string, unknown>): PrayerTimesData | null {
    if (!this.location) return null;

    const timings = serverData.timings as Record<string, string>;
    if (!timings) return null;

    const dateStr = serverData.date as string | undefined;

    const fajr = parseTimeToDate(timings.Fajr, dateStr);
    const sunrise = parseTimeToDate(timings.Sunrise, dateStr);
    const dhuhr = parseTimeToDate(timings.Dhuhr, dateStr);
    const asr = parseTimeToDate(timings.Asr, dateStr);
    const maghrib = parseTimeToDate(timings.Maghrib, dateStr);
    const isha = parseTimeToDate(timings.Isha, dateStr);

    const now = new Date();
    const entries: Array<{ key: PrayerKey; time: Date }> = [
      { key: 'fajr', time: fajr },
      { key: 'sunrise', time: sunrise },
      { key: 'dhuhr', time: dhuhr },
      { key: 'asr', time: asr },
      { key: 'maghrib', time: maghrib },
      { key: 'isha', time: isha },
    ];

    let nextPrayer: NextPrayer | null = null;
    for (const entry of entries) {
      if (entry.time > now) {
        const minutesUntil = Math.round((entry.time.getTime() - now.getTime()) / 60_000);
        nextPrayer = { name: entry.key, time: entry.time, minutesUntil };
        break;
      }
    }

    return {
      fajr,
      sunrise,
      dhuhr,
      asr,
      maghrib,
      isha,
      nextPrayer: nextPrayer ?? this.computeTomorrowFajr(),
      qiblaDirection: this.getQiblaDirection(),
      location: { ...this.location },
      method: this.method,
      source: this.lastSource,
      hijri: serverData.hijri as PrayerTimesData['hijri'],
    };
  }

  // ---- Private helpers ----------------------------------------------------

  private loadFromStorage(): void {
    try {
      const locStr = localStorage.getItem(LS_LOCATION);
      if (locStr) {
        this.location = JSON.parse(locStr);
      }
    } catch {
      // corrupt data
    }

    const method = localStorage.getItem(LS_METHOD) as CalculationMethodKey | null;
    if (method) {
      this.method = method;
    }

    const madhab = localStorage.getItem(LS_MADHAB) as MadhabKey | null;
    if (madhab === 'Shafi' || madhab === 'Hanafi') {
      this.madhab = madhab;
    }

    try {
      const remStr = localStorage.getItem(LS_REMINDERS);
      if (remStr) {
        const parsed = JSON.parse(remStr) as Partial<PrayerReminderConfig>;
        Object.assign(this.reminderOffsets, parsed);
      }
    } catch {
      // corrupt data
    }
  }

  private async requestGeolocation(): Promise<void> {
    if (!navigator.geolocation) return;

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 10_000,
          maximumAge: 600_000,
        });
      });

      this.location = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };
      localStorage.setItem(LS_LOCATION, JSON.stringify(this.location));
    } catch {
      // Geolocation denied or unavailable
    }
  }

  private computeLocalTimes(): void {
    if (!this.location) return;
    const today = new Date().toDateString();
    if (this.cachedDate === today && this.cachedAdhanTimes) return;

    const coords = new Coordinates(this.location.lat, this.location.lng);
    const params = getCalculationParams(this.method, this.madhab, coords);
    this.cachedAdhanTimes = new AdhanPrayerTimes(coords, new Date(), params);
    this.cachedDate = today;
  }

  private getAdhanTimes(): AdhanPrayerTimes | null {
    this.computeLocalTimes();
    return this.cachedAdhanTimes;
  }

  private computeNextPrayer(times: AdhanPrayerTimes): NextPrayer | null {
    const now = new Date();

    const entries: Array<{ key: PrayerKey; time: Date }> = [
      { key: 'fajr', time: times.fajr },
      { key: 'sunrise', time: times.sunrise },
      { key: 'dhuhr', time: times.dhuhr },
      { key: 'asr', time: times.asr },
      { key: 'maghrib', time: times.maghrib },
      { key: 'isha', time: times.isha },
    ];

    for (const entry of entries) {
      if (entry.time > now) {
        const minutesUntil = Math.round((entry.time.getTime() - now.getTime()) / 60_000);
        return {
          name: entry.key,
          time: entry.time,
          minutesUntil,
        };
      }
    }

    return null;
  }

  private showPrayerNotification(prayer: PrayerKey, time: Date): void {
    if (Notification.permission !== 'granted') return;

    const name = this.getPrayerName(prayer);
    const offset = this.reminderOffsets[prayer];
    const timeStr = this.formatTime(time);

    try {
      new Notification(`${name} in ${offset} minutes`, {
        body: `${name} at ${timeStr}`,
        icon: '/icons/icon-192x192.png',
        badge: '/icons/icon-72x72.png',
        tag: `prayer-reminder-${prayer}`,
        silent: false,
      });
    } catch {
      // Notification API not available in this context
    }
  }

  private showAthanNotification(prayer: PrayerKey): void {
    if (Notification.permission !== 'granted') return;

    const name = this.getPrayerName(prayer);

    try {
      new Notification(`${name}`, {
        body: `It is time for ${name} prayer`,
        icon: '/icons/icon-192x192.png',
        badge: '/icons/icon-72x72.png',
        tag: `prayer-athan-${prayer}`,
        silent: false,
      });
    } catch {
      // Notification API not available in this context
    }
  }

  private clearScheduledNotifications(): void {
    for (const id of this.scheduledNotifications) {
      window.clearTimeout(id);
    }
    this.scheduledNotifications = [];
  }
}

// ---------------------------------------------------------------------------
// Singleton
// ---------------------------------------------------------------------------

export const prayerTimeService = new PrayerTimeService();
